import { usePageMeta } from "@/lib/use-page-meta";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { SiteNav } from "@/components/site-nav";
import { SiteFooter } from "@/components/site-footer";
import {
  allVideosQuery,
  aggregatesQuery,
  primaryLocation,
  topCountriesFrom,
  creatorsListQuery,
  type VideoWithLocations,
  videoParam,
} from "@/lib/queries";
import { formatDate, formatCompact, formatDuration, translateCountry, cleanTitle } from "@/lib/format";
import { CountryLabel } from "@/components/country-label";
import { MapView } from "@/components/map-view";
import { LayoutGrid, List as ListIcon, ArrowUp, ArrowDown, Youtube, Search as SearchIcon } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { safeLoad } from "@/lib/ssr-safe";
import "@/i18n";

const searchSchema = z.object({
  q: fallback(z.string(), "").default(""),
  country: fallback(z.string(), "").default(""),
  channel: fallback(z.string(), "").default(""),
  creator: fallback(z.string(), "").default(""),
  year: fallback(z.string(), "").default(""),
  page: fallback(z.number().int(), 1).default(1),
  perPage: fallback(z.number().int(), 50).default(50),
  sort: fallback(z.string(), "published_at").default("published_at"),
  dir: fallback(z.string(), "desc").default("desc"),
  view: fallback(z.string(), "").default(""),
});

/** Defaults are stripped from the URL so shared links stay short and readable. */
type VideoSearch = z.infer<typeof searchSchema>;

const searchDefaults: VideoSearch = {
  q: "",
  country: "",
  channel: "",
  creator: "",
  year: "",
  page: 1,
  perPage: 50,
  sort: "published_at",
  dir: "desc",
  view: "",
};

const SORT_KEYS = ["published_at", "view_count", "duration_seconds", "title", "country", "channel"] as const;

type SortKey = "published_at" | "view_count" | "duration_seconds" | "title" | "country" | "channel";
type SortDir = "asc" | "desc";
type ViewMode = "grid" | "list";

export const Route = createFileRoute("/videos")({
  staticData: { sitemap: true },
  validateSearch: zodValidator(searchSchema),
  head: () => ({
    meta: [
      { title: "Videos · Youtubers Map" },
      { property: "og:title", content: "Videos · Youtubers Map" },
      { property: "og:description", content: "Explora tots els vídeos geolocalitzats." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "description", content: "Explora tots els vídeos geolocalitzats." },
    ],
  }),
  loader: ({ context }) => {
    // Fire-and-forget: these MUST NOT reject unhandled (an unhandled rejection
    // in the worker kills the whole request with a bare 500).
    void safeLoad("videos:all", context.queryClient.ensureQueryData(allVideosQuery));
    void safeLoad("videos:aggregates", context.queryClient.ensureQueryData(aggregatesQuery));
    void safeLoad("videos:creators", context.queryClient.ensureQueryData(creatorsListQuery));
  },

  component: SearchPage,
});

function SearchPage() {
  const { t, i18n } = useTranslation();
  usePageMeta(t("meta.videosTitle"), t("meta.videosDesc"));
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const { data: videos } = useSuspenseQuery(allVideosQuery);
  const { data: agg } = useSuspenseQuery(aggregatesQuery);
  const { data: creators } = useSuspenseQuery(creatorsListQuery);

  const countries = useMemo(() => topCountriesFrom(agg), [agg]);

  // Sort and view live in the URL too, so a shared link restores the exact view.
  const sortKey: SortKey = (SORT_KEYS as readonly string[]).includes(search.sort)
    ? (search.sort as SortKey)
    : "published_at";
  const sortDir: SortDir = search.dir === "asc" ? "asc" : "desc";
  const isMobile = useIsMobile();
  // On phones the grid reads better, so it is the default until the user picks.
  const viewMode: ViewMode =
    search.view === "grid" || search.view === "list" ? search.view : isMobile ? "grid" : "list";
  const setViewMode = (v: ViewMode) => update({ view: v });

  const years = useMemo(() => {
    const s = new Set<number>();
    for (const v of videos) {
      if (v.published_at) s.add(new Date(v.published_at).getFullYear());
    }
    return Array.from(s).sort((a, b) => b - a);
  }, [videos]);

  const creatorIdBySlug = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of creators) m.set(c.slug, c.id);
    return m;
  }, [creators]);

  const creatorById = useMemo(() => {
    const m = new Map<string, { slug: string; name: string; avatar_url: string | null }>();
    for (const c of creators) {
      m.set(c.id, {
        slug: c.slug,
        name: c.name,
        avatar_url: c.avatar_url ?? c.channels[0]?.avatar_url ?? null,
      });
    }
    return m;
  }, [creators]);

  const creatorOf = (v: VideoWithLocations) => {
    const cid = v.youtubers?.creator_id;
    const c = cid ? creatorById.get(cid) : undefined;
    if (c) return c;
    if (!v.youtubers) return null;
    return { slug: v.youtubers.handle, name: v.youtubers.name, avatar_url: v.youtubers.avatar_url };
  };

  const filtered = useMemo(() => {
    const q = search.q.trim().toLowerCase();
    const creatorId = search.creator ? creatorIdBySlug.get(search.creator) : "";
    const arr = videos.filter((v) => {
      const loc = primaryLocation(v);
      if (search.country && loc?.country_code !== search.country) return false;
      if (search.channel && v.youtubers?.handle !== search.channel) return false;
      if (creatorId && v.youtubers?.creator_id !== creatorId) return false;
      if (search.year) {
        const y = v.published_at ? new Date(v.published_at).getFullYear().toString() : "";
        if (y !== search.year) return false;
      }
      if (q) {
        const hay = [
          v.title,
          v.youtubers?.name,
          loc?.country_name,
          loc?.city,
          loc?.region,
          loc?.name,
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    const dir = sortDir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      const la = primaryLocation(a);
      const lb = primaryLocation(b);
      switch (sortKey) {
        case "published_at":
          return dir * (new Date(a.published_at ?? 0).getTime() - new Date(b.published_at ?? 0).getTime());
        case "view_count":
          return dir * ((a.view_count ?? 0) - (b.view_count ?? 0));
        case "duration_seconds":
          return dir * ((a.duration_seconds ?? 0) - (b.duration_seconds ?? 0));
        case "title":
          return dir * a.title.localeCompare(b.title);
        case "country":
          return dir * (la?.country_name ?? "").localeCompare(lb?.country_name ?? "");
        case "channel": {
          const na = a.youtubers?.creator_id ? creatorById.get(a.youtubers.creator_id)?.name : a.youtubers?.name;
          const nb = b.youtubers?.creator_id ? creatorById.get(b.youtubers.creator_id)?.name : b.youtubers?.name;
          return dir * (na ?? "").localeCompare(nb ?? "");
        }
      }
    });
    return arr;
  }, [videos, search, sortKey, sortDir, creatorIdBySlug, creatorById]);

  const stats = useMemo(() => {
    const people = new Set<string>();
    const locs = new Set<string>();
    for (const v of filtered) {
      people.add(v.youtubers?.creator_id ?? v.youtuber_id);
      const l = primaryLocation(v);
      if (l) locs.add([l.city, l.region, l.name].filter(Boolean).join(" · ") || "—");
    }
    return { videos: filtered.length, youtubers: people.size, locations: locs.size };
  }, [filtered]);

  const mapPins = useMemo(
    () =>
      filtered.flatMap((v) => {
        const loc = primaryLocation(v);
        if (!loc || loc.latitude == null || loc.longitude == null) return [];
        return [{
          id: v.id,
          lat: Number(loc.latitude),
          lng: Number(loc.longitude),
          label: cleanTitle(v.title),
          subtitle: [loc.city, translateCountry(loc.country_code, loc.country_name, i18n.language)]
            .filter(Boolean)
            .join(", "),
          href: `/v/${videoParam(v)}`,
          thumbnail: v.thumbnail_url,
          countryCode: loc.country_code,
        }];
      }),
    [filtered, i18n.language],
  );


  // Keys that equal their default are dropped, so shared URLs only carry the
  // filters that are actually active (e.g. /videos?country=JP&view=grid).
  const update = (patch: Partial<VideoSearch>, replace = false) =>
    navigate({
      to: ".",
      replace,
      search: (prev: VideoSearch) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const [key, def] of Object.entries(searchDefaults)) {
          if (next[key] === def) delete next[key];
        }
        return next as VideoSearch;
      },
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / search.perPage));
  const currentPage = Math.min(search.page, totalPages);
  const pageItems = useMemo(
    () => filtered.slice((currentPage - 1) * search.perPage, currentPage * search.perPage),
    [filtered, currentPage, search.perPage],
  );


  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      update({ dir: sortDir === "asc" ? "desc" : "asc" });
    } else {
      update({
        sort: key,
        dir: key === "title" || key === "country" || key === "channel" ? "asc" : "desc",
      });
    }
  };

  return (
    <div className="min-h-screen">
      <SiteNav />
      <section className="max-w-7xl mx-auto px-6 py-8">
        <p className="text-mono-caps text-amber mb-2">{t("search.kicker")}</p>
        <h1 className="text-3xl md:text-4xl mb-2">
          {search.country
            ? t("country.title", { country: translateCountry(search.country, null, i18n.language) })
            : t("search.title")}
        </h1>
        <p className="text-sm text-ink/60 font-mono mb-5">
          {t("country.stats", {
            videos: stats.videos,
            youtubers: stats.youtubers,
            locations: stats.locations,
          })}
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon className="size-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-ink/40" />
            <input
              type="search"
              value={search.q}
              onChange={(e) => update({ q: e.target.value }, true)}
              placeholder={t("search.placeholder")}
              className="w-full bg-paper ring-hairline text-sm pl-8 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber"
            />
          </div>
          <select
            aria-label={t("search.allCountries")}
            value={search.country}
            onChange={(e) => update({ country: e.target.value, page: 1 })}
            className="bg-paper ring-hairline text-sm px-3 py-2 hover:bg-secondary transition-colors cursor-pointer"
          >
            <option value="">{t("search.allCountries")}</option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>
                {translateCountry(c.code, c.name, i18n.language)} ({c.count})
              </option>
            ))}
          </select>
          <select
            aria-label={t("search.allYoutubers")}
            value={search.creator}
            onChange={(e) => update({ creator: e.target.value, page: 1 })}
            className="bg-paper ring-hairline text-sm px-3 py-2 hover:bg-secondary transition-colors cursor-pointer"
          >
            <option value="">{t("search.allYoutubers")}</option>
            {creators.map((c) => (
              <option key={c.id} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>

          <select
            aria-label={t("home.allYears")}
            value={search.year}
            onChange={(e) => update({ year: e.target.value })}
            className="bg-paper ring-hairline text-sm px-3 py-2 hover:bg-secondary transition-colors cursor-pointer"
          >
            <option value="">{t("home.allYears")}</option>
            {years.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
          <div className="flex items-center ring-hairline bg-paper">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 ${viewMode === "list" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
              aria-label={t("common.viewList")}
            >
              <ListIcon className="size-4" />
            </button>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 ${viewMode === "grid" ? "bg-ink text-paper" : "hover:bg-secondary"}`}
              aria-label={t("common.viewGrid")}
            >
              <LayoutGrid className="size-4" />
            </button>
          </div>
        </div>
      </section>

      {search.country && mapPins.length > 0 && (
        <section className="max-w-7xl mx-auto px-6">
          <div className="ring-hairline bg-paper overflow-hidden">
            <MapView pins={mapPins} mode="points" className="h-[380px] w-full" pinSize={8} />
          </div>
        </section>
      )}


      <section className="max-w-7xl mx-auto px-6 py-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <p className="text-mono-caps text-ink/50">
            {t("search.results", { count: filtered.length })}
          </p>
          <div className="flex items-center gap-2 text-xs font-mono text-ink/60">
            <label>
              {t("common.perPage")}:{" "}
              <select
                aria-label={t("common.perPage")}
                value={search.perPage}
                onChange={(e) => update({ perPage: Number(e.target.value), page: 1 })}
                className="bg-paper ring-hairline px-2 py-1 cursor-pointer"
              >
                {[25, 50, 100, 200].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
        {viewMode === "list" ? (
          <VideoTable
            videos={pageItems}
            sortKey={sortKey}
            sortDir={sortDir}
            onToggleSort={toggleSort}
            creatorOf={creatorOf}
          />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {pageItems.map((v) => {
              const loc = primaryLocation(v);
              return (
                <Link
                  key={v.id}
                  to="/v/$slug"
                  params={{ slug: videoParam(v) }}
                  className="group bg-paper ring-hairline overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="aspect-video bg-secondary overflow-hidden">
                    {v.thumbnail_url && (
                      <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    )}
                  </div>
                  <div className="p-3">
                    <h4 className="font-serif text-sm line-clamp-2 mb-2 group-hover:text-amber transition-colors">
                      {cleanTitle(v.title)}
                    </h4>
                    <div className="flex items-center justify-between text-[10px] font-mono text-ink/50">
                      <span className="truncate">{v.youtubers?.name}</span>
                      <span>{formatCompact(v.view_count)}</span>
                    </div>
                    {loc?.country_code && (
                      <div className="mt-1">
                        <CountryLabel code={loc.country_code} name={loc.country_name} className="text-[10px] text-amber" size={20} />
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-2 text-xs font-mono">
            <button
              onClick={() => update({ page: Math.max(1, search.page - 1) })}
              disabled={search.page <= 1}
              className="ring-hairline px-3 py-1.5 bg-paper hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← {t("common.prev")}
            </button>
            <span className="text-ink/60 px-2">
              {search.page} / {totalPages}
            </span>
            <button
              onClick={() => update({ page: Math.min(totalPages, search.page + 1) })}
              disabled={search.page >= totalPages}
              className="ring-hairline px-3 py-1.5 bg-paper hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {t("common.next")} →
            </button>
          </div>
        )}
      </section>
      <SiteFooter />
    </div>
  );
}

function VideoTable({
  videos,
  sortKey,
  sortDir,
  onToggleSort,
  creatorOf,
}: {
  videos: VideoWithLocations[];
  sortKey: SortKey;
  sortDir: SortDir;
  onToggleSort: (key: SortKey) => void;
  creatorOf: (v: VideoWithLocations) => { slug: string; name: string; avatar_url: string | null } | null;
}) {
  const { t, i18n } = useTranslation();
  const SortHead = ({ k, label, className = "" }: { k: SortKey; label: string; className?: string }) => (
    <th className={`text-left font-mono uppercase text-[10px] tracking-wider text-ink/50 px-3 py-2 ${className}`}>
      <button onClick={() => onToggleSort(k)} className="inline-flex items-center gap-1 hover:text-ink">
        {label}
        {sortKey === k && (sortDir === "asc" ? <ArrowUp className="size-3" /> : <ArrowDown className="size-3" />)}
      </button>
    </th>
  );
  return (
    <div className="ring-hairline bg-paper overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="border-b border-ink/10 bg-secondary/40">
          <tr>
            <th className="w-16 px-3 py-2" />
            <SortHead k="title" label={t("common.title")} />
            <SortHead k="channel" label={t("common.youtuber")} className="w-40" />
            <SortHead k="country" label={t("common.country")} className="w-44" />
            <SortHead k="published_at" label={t("common.date")} className="w-28" />
            <SortHead k="view_count" label={t("common.views")} className="w-24" />
            <SortHead k="duration_seconds" label={t("common.duration")} className="w-24" />
            <th className="w-10" />
          </tr>
        </thead>
        <tbody className="divide-y divide-ink/8">
          {videos.map((v) => {
            const loc = primaryLocation(v);
            return (
              <tr key={v.id} className="hover:bg-secondary/40 transition-colors">
                <td className="px-3 py-1.5">
                  <Link to="/v/$slug" params={{ slug: videoParam(v) }} className="block w-16 aspect-video bg-secondary overflow-hidden">
                    {v.thumbnail_url && (
                      <img src={v.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    )}
                  </Link>
                </td>
                <td className="px-3 py-1.5">
                  <Link to="/v/$slug" params={{ slug: videoParam(v) }} className="font-serif hover:text-amber line-clamp-1">
                    {cleanTitle(v.title)}
                  </Link>
                </td>
                <td className="px-3 py-1.5 text-ink/70 whitespace-nowrap">
                  {(() => {
                    const c = creatorOf(v);
                    return c ? (
                      <Link
                        to="/y/$handle"
                        params={{ handle: c.slug }}
                        className="inline-flex items-center gap-2 group"
                      >
                        {c.avatar_url ? (
                          <img
                            src={c.avatar_url}
                            alt=""
                            loading="lazy"
                            className="size-6 rounded-full object-cover ring-1 ring-ink/10 flex-shrink-0"
                          />
                        ) : (
                          <span className="size-6 rounded-full bg-secondary ring-1 ring-ink/10 grid place-items-center text-[10px] font-mono text-ink/50 flex-shrink-0">
                            {c.name.slice(0, 1).toUpperCase()}
                          </span>
                        )}
                        <span className="font-medium group-hover:text-amber truncate max-w-[9rem]">
                          {c.name}
                        </span>
                      </Link>
                    ) : "—";
                  })()}
                </td>
                <td className="px-3 py-1.5 text-ink/70 whitespace-nowrap">
                  {loc?.country_code ? (
                    <Link
                      to="/country/$code"
                      params={{ code: loc.country_code.toLowerCase() }}
                      className="hover:text-amber inline-flex"
                    >
                      <CountryLabel code={loc.country_code} name={loc.country_name} size={20} />
                    </Link>
                  ) : "—"}
                </td>
                <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap">{formatDate(v.published_at, i18n.language)}</td>
                <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">
                  {formatCompact(v.view_count)}
                </td>
                <td className="px-3 py-1.5 text-ink/60 whitespace-nowrap font-mono text-xs">
                  {v.duration_seconds != null ? formatDuration(v.duration_seconds) : "—"}
                </td>
                <td className="px-3 py-1.5">
                  <a
                    href={`https://youtube.com/watch?v=${v.youtube_video_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ink/40 hover:text-amber inline-flex"
                    title={t("common.openInYoutube")}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Youtube className="size-4" />
                  </a>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
